/**
 * Download a challenge, paste the answer.
 *
 * Every write the recovery shares authorise needs this, so it lives once rather
 * than beside each of them. The server cannot check that a wrapped key is the
 * right one — checking would mean being able to open it — so it checks who is
 * asking: it seals a random number to the recovery public key, and only the
 * private half, rebuilt from two shares and opened with the passphrase, can
 * hand the number back.
 *
 * The two steps are shown in the order they happen, with the command between
 * them, because the middle step is on a different machine and somebody doing
 * this for the first time in three years should not have to go and find it.
 */

import { useState } from "react";
import { errorMessage, trpcClient } from "../../lib/trpc.js";
import { Button } from "../../components/ui/Button.js";
import { Field } from "../../components/ui/Field.js";
import { Input } from "../../components/ui/Input.js";

export interface ChallengeState {
  token: string;
  answer: string;
}

/** Whether an answer is worth sending: a token issued, and something pasted. */
export function challengeReady(state: ChallengeState | null): boolean {
  return Boolean(state?.token) && (state?.answer.trim().length ?? 0) >= 16;
}

export function ChallengeFields({
  id,
  state,
  onChange,
}: {
  /** Distinguishes the two of these when both are on screen. */
  id: string;
  state: ChallengeState | null;
  onChange: (state: ChallengeState | null) => void;
}) {
  const [asking, setAsking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function ask(): Promise<void> {
    setAsking(true);
    setError(null);

    try {
      const issued = await trpcClient.admin.recoveryChallenge.mutate();
      onChange({ token: issued.token, answer: "" });

      /* Downloaded, because the offline tool reads a file rather than a paste. */
      const blob = new Blob([`${JSON.stringify(issued, null, 2)}\n`], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = "challenge.json";
      anchor.click();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setAsking(false);
    }
  }

  return (
    <>
      <div className="fc-security__export">
        <Button variant="secondary" loading={asking} onClick={() => void ask()}>
          {state ? "New challenge" : "Download challenge"}
        </Button>

        {state && (
          <span className="fc-admin__hint">
            Run <code>fc-recovery answer --challenge challenge.json</code>
          </span>
        )}
      </div>

      {error && (
        <p className="fc-admin__error" role="alert">
          {error}
        </p>
      )}

      <Field label="Answer from the offline tool" htmlFor={`fc-answer-${id}`} required>
        <Input
          id={`fc-answer-${id}`}
          value={state?.answer ?? ""}
          spellCheck={false}
          autoComplete="off"
          /* Disabled until there is something to answer: pasting first and
             pressing Download second silently discards what was typed. */
          disabled={!state}
          placeholder="Paste the contents of answer.txt"
          onChange={(event) =>
            onChange(state ? { ...state, answer: event.target.value } : null)
          }
        />
      </Field>
    </>
  );
}
